'use client'

import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import { Card } from '@acko/card'
import { Typography } from '@acko/typography'
import { ArrowRight } from '@acko/icons'
import { TAP_BTN_ENGAGE } from '@constants/homeSegmentEvent'
import { useEffect, useState } from 'react'
import styles from './page.module.css'

function useSlidesPerView() {
  const [count, setCount] = useState(1)
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 768px) and (max-width: 1023px)')
    const apply = (matches: boolean) => setCount(matches ? 2 : 1)
    apply(mql.matches)
    const handler = (e: MediaQueryListEvent) => apply(e.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])
  return count
}

/* ─── Educational Articles Images (bike insurance guides and tips) ─── */
const imgArticle1 = 'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/bike_insurance_plans_4b1f0a58eb.webp'
const imgArticle2 = 'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/buying_new_bike_article_48bc926fef.webp'
const imgArticle3 = 'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/comp_vs_third_party_5295a6bc73.webp'
const imgCircleArrowDisabled = 'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/Circle_arrow_disabled_88cebae0d6.svg'
const imgCircleArrowFilled = 'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/Circle_arrow_filled_9d8fd2ddfd.svg'

/**
 * Articles Section - Educational bike insurance content
 * Displays helpful guides and tips about bike insurance (plans, buying tips, coverage comparison)
 * This is separate from NewsCoverageSectionClient which shows ACKO company news
 */
export const ArticlesSectionClient = () => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const slidesPerView = useSlidesPerView()

  const articles = [
    {
      img: imgArticle3,
      title: 'How to switch from Third-party to Comprehensive bike insurance?',
      author: 'Team Acko',
      readTime: '2 min read',
      redirectLink: '/two-wheeler-insurance/switch-third-party-insurance-to-comprehensive-bike-insurance/',
    },
    {
      img: imgArticle2,
      title: 'How to Renew Bike Insurance Online in India: Step-by-Step Guide',
      author: 'Nikhila PS',
      readTime: '2 min read',
      redirectLink: '/two-wheeler-insurance/blogs/how-to-renew-bike-insurance-online-in-india/',
    },
    {
      img: imgArticle1,
      title: 'Bike Insurance Claim Process',
      author: 'Nikhila PS',
      readTime: '2 min read',
      redirectLink: '/two-wheeler-insurance/claims/',
    },
  ]

  const maxSlide = Math.max(0, articles.length - slidesPerView)
  const pageCount = articles.length - slidesPerView + 1

  useEffect(() => {
    setCurrentSlide(prev => Math.min(prev, maxSlide))
  }, [maxSlide])

  const nextSlide = () => {
    setCurrentSlide(prev => Math.min(prev + 1, maxSlide))
  }

  const prevSlide = () => {
    setCurrentSlide(prev => Math.max(prev - 1, 0))
  }

  const goToSlide = (index: number) => {
    setCurrentSlide(Math.min(Math.max(0, index), maxSlide))
  }

  const trackArticleRedirection = (title: string, redirectionUrl: string) => {
    void trackEvent(TAP_BTN_ENGAGE, {
      component_name: 'article_redirection',
      cta_text: title,
      redirection_url: redirectionUrl,
    })
  }

  const trackArticleCarousel = (ctaText: string) => {
    void trackEvent(TAP_BTN_ENGAGE, {
      component_name: 'article_carousel',
      cta_text: ctaText,
    })
  }

  return (
    <section className={styles.articleSectionBg}>
      <div
        className={`${styles.articlesInner} mx-auto flex flex-col gap-32 lg:gap-40 px-16 md:px-32 lg:px-0 pt-40 pb-40 lg:pt-64 lg:pb-64 min-w-0`}>
        <Typography scale="4xl" emphasis="bold" as="h2" color="primary">
          All you need to know about
          <span className="lg:inline block"> bike insurance</span>
        </Typography>

        <div className="lg:hidden">
          <div className="relative overflow-hidden">
            <div
              className="flex transition-transform duration-300 ease-in-out"
              style={{ transform: `translateX(-${currentSlide * (100 / slidesPerView)}%)` }}>
              {articles.map((article, index) => (
                <div key={index} className="w-full md:w-1/2 shrink-0 px-4">
                  <a
                    href={article.redirectLink}
                    aria-label={`Read ${article.title}`}
                    onClick={() => trackArticleRedirection(article.title, article.redirectLink)}
                    className="group block h-full cursor-pointer no-underline">
                    <Card variant="primary" className="p-20 flex h-full flex-col gap-24">
                      <div className={`${styles.articleImageRatio} relative rounded-[12px] w-full overflow-hidden`}>
                        <img alt="" className="w-full h-full object-cover" src={article.img} />
                      </div>
                      <div className="flex flex-col gap-12">
                        <Typography scale="xl" emphasis="medium" as="h3" color="primary" className="leading-tight">
                          {article.title}
                        </Typography>
                        <Typography scale="base" color="secondary">
                          {article.author} • {article.readTime}
                        </Typography>
                      </div>
                      <div className="flex items-center gap-4">
                        <Typography scale="base" emphasis="medium" as="span" color="primary">
                          Know more
                        </Typography>
                        <ArrowRight
                          aria-hidden
                          className="w-20 h-20 text-current transition-transform duration-200 group-hover:translate-x-4"
                        />
                      </div>
                    </Card>
                  </a>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between mt-16">
            <div className="flex gap-4">
              {Array.from({ length: pageCount }).map((_, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => {
                    trackArticleCarousel(`Article ${index + 1}`)
                    goToSlide(index)
                  }}
                  className={`w-8 h-8 rounded-full transition-colors duration-200 ${
                    index === currentSlide ? 'bg-black' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>

            <div className="flex gap-12">
              <button
                type="button"
                onClick={() => {
                  trackArticleCarousel('Previous article')
                  prevSlide()
                }}
                className="w-40 h-40 flex items-center justify-center transition-opacity duration-200">
                <img
                  alt="Previous"
                  className="w-full h-full"
                  src={currentSlide === 0 ? imgCircleArrowDisabled : imgCircleArrowFilled}
                  style={{
                    transform: currentSlide === 0 ? 'none' : 'rotate(180deg)',
                    opacity: currentSlide === 0 ? 0.5 : 1,
                  }}
                />
              </button>
              <button
                type="button"
                onClick={() => {
                  trackArticleCarousel('Next article')
                  nextSlide()
                }}
                className="w-40 h-40 flex items-center justify-center transition-opacity duration-200">
                <img
                  alt="Next"
                  className="w-full h-full"
                  src={currentSlide === maxSlide ? imgCircleArrowDisabled : imgCircleArrowFilled}
                  style={{
                    transform: currentSlide === maxSlide ? 'rotate(180deg)' : 'none',
                    opacity: currentSlide === maxSlide ? 0.5 : 1,
                  }}
                />
              </button>
            </div>
          </div>
        </div>

        <div className="hidden lg:flex flex-row gap-24 items-stretch w-full">
          {articles.map((article, index) => (
            <a
              key={index}
              href={article.redirectLink}
              aria-label={`Read ${article.title}`}
              onClick={() => trackArticleRedirection(article.title, article.redirectLink)}
              className={`group ${styles.minW0} flex-1 cursor-pointer no-underline`}>
              <Card variant="primary" className={`p-24 ${styles.minW0} flex h-full flex-col gap-24`}>
                <div className={`${styles.articleImageRatio} relative rounded-[12px] w-full overflow-hidden shrink-0`}>
                  <img alt="" className="w-full h-full object-cover" src={article.img} />
                </div>
                <div className="flex flex-col gap-12 justify-between h-full">
                  <div className="flex flex-col gap-12 justify-between h-full">
                    <Typography scale="xl" emphasis="medium" as="h3" color="primary">
                      {article.title}
                    </Typography>
                    <Typography scale="base" color="secondary">
                      {article.author} • {article.readTime}
                    </Typography>
                  </div>
                  <div className="flex items-center gap-4">
                    <Typography scale="base" emphasis="medium" as="span" color="primary">
                      Know more
                    </Typography>
                    <ArrowRight
                      aria-hidden
                      className="w-20 h-20 text-current transition-transform duration-200 group-hover:translate-x-4"
                    />
                  </div>
                </div>
              </Card>
            </a>
          ))}
        </div>
      </div>
    </section>
  )
}
